import { useLayoutEffect, useRef } from "react";
import { mailDom, type MailScheme } from "./mail-dom";

/**
 * A mail as the customer's mail app shows it, with its own layout, in its white or its dark version.
 * It lives in a shadow root: the till's styles do not reach the mail, and the mail's do not reach the till.
 *
 * A mail is laid out some 600 px wide. In a narrower window (a phone) it is drawn smaller as a whole, as a
 * mail app on a phone does, instead of running off the side (Furkan, 18/10/2026: the Dagpromo did not fit).
 */
export function MailPreview({ html, scheme }: { html: string; scheme: MailScheme }) {
  const host = useRef<HTMLDivElement>(null);

  // before the window is painted: the mail is never seen as an empty sheet
  useLayoutEffect(() => {
    const element = host.current;
    if (element === null) return;
    const root = element.shadowRoot ?? element.attachShadow({ mode: "open" });
    const sheet = mailDom(html, scheme);
    root.replaceChildren(sheet);

    const fit = () => {
      sheet.style.removeProperty("zoom");
      const room = element.clientWidth;
      const needed = sheet.scrollWidth;
      if (room > 0 && needed > room + 1) sheet.style.setProperty("zoom", String(room / needed));
    };
    fit();
    if (typeof ResizeObserver === "undefined") return;
    const watch = new ResizeObserver(fit);
    watch.observe(element);
    return () => {
      watch.disconnect();
    };
  }, [html, scheme]);

  return <div className="mailpaper" ref={host} data-scheme={scheme} />;
}
